import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import type { MedicalRecord } from '../../../../types/animal';

interface MedicalRecordDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  medicalRecordEditMode: boolean;
  selectedMedicalRecord: MedicalRecord | null;
  setSelectedMedicalRecord: (record: MedicalRecord) => void;
  handleEditMedicalRecord: () => void;
  handleCloseMedicalRecordDialog: () => void;
  handleSaveEditedMedicalRecord: () => void;
  handleDeleteMedicalRecord: () => void;
  medicalRecordLoading: boolean;
  medicalRecordTypes: string[];
}

const MedicalRecordDetailsDialog: React.FC<MedicalRecordDetailsDialogProps> = ({
  open,
  onClose,
  medicalRecordEditMode,
  selectedMedicalRecord,
  setSelectedMedicalRecord,
  handleEditMedicalRecord,
  handleCloseMedicalRecordDialog,
  handleSaveEditedMedicalRecord,
  handleDeleteMedicalRecord,
  medicalRecordLoading,
  medicalRecordTypes,
}) => {
  return (
    <Dialog 
      open={open}
      onClose={handleCloseMedicalRecordDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {medicalRecordEditMode ? 'Edit Medical Record' : 'Medical Record Details'}
      </DialogTitle>
      <DialogContent>
        {selectedMedicalRecord && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={selectedMedicalRecord.date}
                onChange={e => setSelectedMedicalRecord({ ...selectedMedicalRecord, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                disabled={!medicalRecordEditMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Record Type"
                value={selectedMedicalRecord.type}
                onChange={e => setSelectedMedicalRecord({ ...selectedMedicalRecord, type: e.target.value as any })}
                required
                disabled={!medicalRecordEditMode}
              >
                {medicalRecordTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={selectedMedicalRecord.title}
                onChange={e => setSelectedMedicalRecord({ ...selectedMedicalRecord, title: e.target.value })}
                required
                disabled={!medicalRecordEditMode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={selectedMedicalRecord.description}
                onChange={e => setSelectedMedicalRecord({ ...selectedMedicalRecord, description: e.target.value })}
                required
                disabled={!medicalRecordEditMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Performed By"
                value={selectedMedicalRecord.performedBy}
                onChange={e => setSelectedMedicalRecord({ ...selectedMedicalRecord, performedBy: e.target.value })}
                required
                disabled={!medicalRecordEditMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={selectedMedicalRecord.status}
                onChange={e => setSelectedMedicalRecord({ ...selectedMedicalRecord, status: e.target.value as any })}
                required
                disabled={!medicalRecordEditMode}
              >
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="ongoing">Ongoing</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Cost (Ksh)"
                value={selectedMedicalRecord.cost || ''}
                onChange={e => setSelectedMedicalRecord({ ...selectedMedicalRecord, cost: Number(e.target.value) })}
                InputProps={{ inputProps: { min: 0 } }}
                disabled={!medicalRecordEditMode}
              />
            </Grid>
            {(selectedMedicalRecord.type === 'treatment' || selectedMedicalRecord.type === 'vaccination') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Medications/Vaccines"
                  value={selectedMedicalRecord.medications ? selectedMedicalRecord.medications.map(m => m.name).join(', ') : ''}
                  onChange={e => setSelectedMedicalRecord({ ...selectedMedicalRecord, medications: e.target.value.split(',').map(m => ({ name: m.trim(), dosage: '', frequency: '' })) })}
                  placeholder="Enter medications separated by commas"
                  disabled={!medicalRecordEditMode}
                />
              </Grid>
            )}
            {selectedMedicalRecord.status === 'completed' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Result/Outcome"
                  value={selectedMedicalRecord.result || ''}
                  onChange={e => setSelectedMedicalRecord({ ...selectedMedicalRecord, result: e.target.value })}
                  placeholder="Describe the outcome or result of this treatment/examination"
                  multiline
                  rows={2}
                  disabled={!medicalRecordEditMode}
                />
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        {medicalRecordEditMode ? (
          <>
            <Button onClick={handleCloseMedicalRecordDialog} disabled={medicalRecordLoading}>Cancel</Button>
            <Button onClick={handleSaveEditedMedicalRecord} variant="contained" color="primary" disabled={medicalRecordLoading}>
              Save
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleCloseMedicalRecordDialog}>Close</Button>
            <Button onClick={handleEditMedicalRecord} variant="outlined" color="primary" startIcon={<EditIcon />}>
              Edit
            </Button>
            <Button onClick={handleDeleteMedicalRecord} variant="contained" color="error" disabled={medicalRecordLoading} startIcon={<DeleteIcon />}>
              Delete
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MedicalRecordDetailsDialog;
